import axios, {
  CancelToken as IAxiosCancelToken,
  CancelTokenSource as IAxiosCancelTokenSrc,
} from "axios";
import { useCallback, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

export type CancelToken = {
  id: string;
  isCanceled?: boolean;
} & IAxiosCancelTokenSrc;

const cancelRequestByToken = (token: CancelToken, componentName?: string) => {
  if (token.isCanceled) return;

  token.cancel(
    `${componentName ?? ""} Canceling request with token id ${token.id}`
  );
  token.isCanceled = true;
};

const createToken = () => {
  const newToken = AxiosCancelToken.source();
  const newTokenId = uuidv4();
  const newCancelToken = { ...newToken, id: newTokenId };

  return newCancelToken;
};

const { CancelToken: AxiosCancelToken } = axios;

/**
 * Hook that provide cancel tokens for HTTP requests.
 * This hook also "Unsubscribe" from all requests when a component unmounts
 */
const useCancelRequests = (componentName?: string) => {
  const tokens = useRef<CancelToken[]>();

  const getToken = useCallback((): {
    id: string;
    cancelToken: IAxiosCancelToken;
  } => {
    if (!tokens.current?.length) {
      tokens.current = [];
    }
    const newCancelToken = createToken();
    tokens.current.push(newCancelToken);

    return { id: newCancelToken.id, cancelToken: newCancelToken.token };
  }, []);

  const cancelRequest = useCallback(
    (tokenId: string) => {
      if (!tokens.current) return;

      const token = tokens.current.find((t) => t.id === tokenId);

      if (!token) return;

      cancelRequestByToken(token, componentName);
    },
    [componentName]
  );

  const isCanceled = useCallback((tokenId: string) => {
    const token = tokens.current?.find((t) => t.id === tokenId);
    return token?.isCanceled || token?.token.reason;
  }, []);

  const disposeToken = useCallback((tokenId: string) => {
    const indexOfToken =
      tokens.current?.findIndex((t) => t.id === tokenId) ?? -1;

    if (indexOfToken > -1) {
      cancelRequestByToken(tokens.current![indexOfToken]);
      tokens.current?.splice(indexOfToken, 1);
    }
  }, []);

  useEffect(() => {
    // On component unmount, clear all ongoing requests
    return () => {
      if (!tokens.current) return;
      const tokensToCancel = tokens.current.filter((t) => !isCanceled(t.id));
      for (let i = 0; i < tokensToCancel.length; i++) {
        const token = tokensToCancel[i];
        cancelRequestByToken(token, componentName);
      }
      // Reset tokens
      tokens.current = undefined;
    };
  }, [componentName, isCanceled]);

  return { getToken, cancelRequest, isCanceled, disposeToken };
};

export default useCancelRequests;
